import { useState, useEffect } from "react";
import { resolveVariables } from "../api";
import type { VariableResolution as VarRes, ResolutionPath } from "../api";
import type { ExtractedInsight } from "./ExpertInterview";

/* ── Types ── */
interface Props {
  insights: ExtractedInsight[];
  expertName: string;
}

/* ── Path config ── */
const PATH_META: Record<string, { label: string; icon: string; color: string; desc: string }> = {
  data_match:  { label: "Data Match",  icon: "DB",  color: "#2563eb", desc: "Found in your dataset" },
  proxy:       { label: "Proxy",       icon: "PX",  color: "#7c3aed", desc: "Similar variable available" },
  correlation: { label: "Correlation", icon: "CR",  color: "#0891b2", desc: "Correlates with existing features" },
  synthetic:   { label: "Synthetic",   icon: "SN",  color: "#059669", desc: "Generate from expert prior" },
};

/* ── Score bar ── */
function ScoreBar({ score, color }: { score: number; color: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 4, background: "rgba(0,0,0,0.06)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${score * 100}%`, background: color, borderRadius: 2, transition: "width 0.4s ease" }} />
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color, minWidth: 28 }}>
        {(score * 100).toFixed(0)}%
      </span>
    </div>
  );
}

/* ── Path Card ── */
function PathCard({ path, isRecommended, isSelected, onSelect }: {
  path: ResolutionPath;
  isRecommended: boolean;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const meta = PATH_META[path.type];
  return (
    <div
      onClick={onSelect}
      style={{
        padding: "14px 16px", borderRadius: 10, cursor: "pointer",
        border: `1.5px solid ${isSelected ? meta.color : isRecommended ? meta.color + "40" : "rgba(0,0,0,0.08)"}`,
        background: isSelected ? meta.color + "08" : "#fff",
        transition: "all 0.15s ease",
        position: "relative",
      }}
    >
      {isRecommended && (
        <span style={{
          position: "absolute", top: -8, right: 12,
          fontSize: 8, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
          padding: "2px 8px", borderRadius: 20,
          background: meta.color, color: "#fff",
        }}>
          Recommended
        </span>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center",
          background: meta.color + "14", color: meta.color, fontSize: 10, fontWeight: 800,
        }}>
          {meta.icon}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700 }}>{meta.label}</div>
          <div style={{ fontSize: 9, color: "rgba(0,0,0,0.4)" }}>{meta.desc}</div>
        </div>
      </div>

      <ScoreBar score={path.score} color={meta.color} />

      <div style={{ marginTop: 10, fontSize: 11, color: "rgba(0,0,0,0.6)", lineHeight: 1.5 }}>
        {path.reason}
      </div>

      {/* Path-specific details */}
      {path.type === "data_match" && path.matched_column && (
        <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 6, background: "rgba(0,0,0,0.03)", fontSize: 11 }}>
          <span style={{ color: "rgba(0,0,0,0.4)" }}>Matched: </span>
          <span style={{ fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{path.matched_column}</span>
        </div>
      )}

      {path.type === "proxy" && path.proxy_columns && path.proxy_columns.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
          {path.proxy_columns.map(col => (
            <span key={col} style={{
              fontSize: 10, padding: "2px 8px", borderRadius: 20,
              background: "rgba(124,58,237,0.08)", color: "#7c3aed", fontWeight: 600,
              fontFamily: "'IBM Plex Mono', monospace",
            }}>{col}</span>
          ))}
        </div>
      )}

      {path.type === "correlation" && path.top_correlates && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 2 }}>
          {path.top_correlates.slice(0, 3).map(tc => (
            <div key={tc.col} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              fontSize: 10, padding: "3px 8px", borderRadius: 4, background: "rgba(0,0,0,0.02)",
            }}>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 500 }}>{tc.col}</span>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, color: Math.abs(tc.r) > 0.3 ? "#0891b2" : "rgba(0,0,0,0.3)" }}>
                r={tc.r.toFixed(3)}
              </span>
            </div>
          ))}
        </div>
      )}

      {path.type === "synthetic" && path.prior && (
        <div style={{ marginTop: 8, display: "flex", gap: 12 }}>
          {[
            ["N(μ, σ)", `N(${path.prior.mean.toFixed(3)}, ${path.prior.std.toFixed(3)})`],
            ["Equiv.", `~${path.sample_equiv} obs`],
          ].map(([label, val]) => (
            <div key={label as string} style={{ fontSize: 10 }}>
              <span style={{ color: "rgba(0,0,0,0.4)" }}>{label}: </span>
              <span style={{ fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{val}</span>
            </div>
          ))}
        </div>
      )}

      {isSelected && (
        <div style={{
          marginTop: 10, padding: "6px 0", textAlign: "center",
          fontSize: 10, fontWeight: 700, color: meta.color, textTransform: "uppercase", letterSpacing: "0.06em",
        }}>
          Selected
        </div>
      )}
    </div>
  );
}

/* ── Main Component ── */
export default function VariableResolutionTab({ insights, expertName }: Props) {
  const [resolutions, setResolutions] = useState<VarRes[]>([]);
  const [datasetInfo, setDatasetInfo] = useState<{ columns: string[]; n_rows: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedIdx, setExpandedIdx] = useState<number>(0);
  const [selections, setSelections] = useState<Record<string, string>>({});

  useEffect(() => {
    const vars = insights
      .filter(ins => ins.variable_name)
      .map(ins => ({
        variable_name: ins.variable_name,
        description: ins.text ?? "",
        mechanism: ins.mechanism ?? "",
        source_quote: ins.source_quote ?? "",
        certainty: ins.confidence ?? 0,
        prior_mean: ins.prior_mean ?? null,
        prior_std: ins.prior_std ?? null,
      }));

    if (vars.length === 0) {
      setLoading(false);
      return;
    }

    resolveVariables(vars as any)
      .then(res => {
        setResolutions(res.data.resolutions);
        setDatasetInfo({ columns: res.data.dataset_columns, n_rows: res.data.n_rows });
        // Auto-select recommended paths
        const sel: Record<string, string> = {};
        res.data.resolutions.forEach(r => { sel[r.variable_name] = r.recommended; });
        setSelections(sel);
      })
      .catch(() => {
        // Fallback: show insights without resolution
        setResolutions(insights.filter(ins => ins.variable_name).map(ins => ({
          variable_name: ins.variable_name ?? "Unknown",
          description: ins.text ?? "",
          mechanism: ins.mechanism ?? "",
          source_quote: ins.source_quote ?? "",
          certainty: ins.confidence ?? 0,
          prior_mean: ins.prior_mean ?? null,
          prior_std: ins.prior_std ?? null,
          paths: [],
          recommended: "",
        })));
      })
      .finally(() => setLoading(false));
  }, [insights]);

  const resolvedCount = Object.keys(selections).length;
  const totalVars = resolutions.length;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#fff" }}>
      {/* Header */}
      <div style={{
        padding: "20px 32px 14px",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            Variable Resolution
            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 10px", borderRadius: 20, background: "rgba(0,0,0,0.06)" }}>
              {resolvedCount} / {totalVars} resolved
            </span>
          </h2>
          <p style={{ fontSize: 11, color: "rgba(0,0,0,0.45)", margin: "3px 0 0" }}>
            {expertName} interview — match each variable to a data resolution path
          </p>
        </div>
        {datasetInfo && (
          <div style={{
            display: "flex", gap: 12, alignItems: "center",
            padding: "6px 14px", borderRadius: 8, background: "rgba(0,0,0,0.03)",
          }}>
            <div style={{ fontSize: 10 }}>
              <span style={{ color: "rgba(0,0,0,0.4)" }}>Dataset: </span>
              <span style={{ fontWeight: 700 }}>ds_demo_001</span>
            </div>
            <div style={{ fontSize: 10 }}>
              <span style={{ color: "rgba(0,0,0,0.4)" }}>Rows: </span>
              <span style={{ fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{datasetInfo.n_rows.toLocaleString()}</span>
            </div>
            <div style={{ fontSize: 10 }}>
              <span style={{ color: "rgba(0,0,0,0.4)" }}>Columns: </span>
              <span style={{ fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{datasetInfo.columns.length}</span>
            </div>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div style={{ height: 2, background: "rgba(0,0,0,0.04)", flexShrink: 0 }}>
        <div style={{
          height: "100%", background: "#000",
          width: `${totalVars > 0 ? (resolvedCount / totalVars) * 100 : 0}%`,
          transition: "width 0.5s ease",
        }} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: "auto", padding: "20px 32px 40px" }}>
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200, gap: 10 }}>
            <div style={{
              width: 16, height: 16, borderRadius: "50%", border: "2px solid #000",
              borderTopColor: "transparent", animation: "spin 0.8s linear infinite",
            }} />
            <span style={{ fontSize: 13, color: "rgba(0,0,0,0.5)" }}>Resolving variables against dataset...</span>
          </div>
        ) : resolutions.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "rgba(0,0,0,0.4)" }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>No variables extracted</div>
            <div style={{ fontSize: 12 }}>Run an expert interview to extract variables first.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 800 }}>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            {resolutions.map((res, idx) => {
              const isExpanded = expandedIdx === idx;
              const selectedPath = selections[res.variable_name];

              return (
                <div key={res.variable_name} style={{
                  border: "1px solid rgba(0,0,0,0.08)", borderRadius: 12,
                  overflow: "hidden", background: "#fff",
                  transition: "all 0.2s ease",
                  boxShadow: isExpanded ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
                }}>
                  {/* Card header */}
                  <div
                    onClick={() => setExpandedIdx(isExpanded ? -1 : idx)}
                    style={{
                      padding: "14px 18px", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 14,
                    }}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: 8,
                      background: selectedPath ? PATH_META[selectedPath]?.color + "14" : "rgba(0,0,0,0.04)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 14, fontWeight: 800,
                      color: selectedPath ? PATH_META[selectedPath]?.color : "rgba(0,0,0,0.3)",
                    }}>
                      {idx + 1}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                        {res.variable_name}
                        {selectedPath && (
                          <span style={{
                            fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                            background: PATH_META[selectedPath]?.color + "14",
                            color: PATH_META[selectedPath]?.color,
                          }}>
                            {PATH_META[selectedPath]?.label}
                          </span>
                        )}
                      </div>
                      {res.source_quote && (
                        <div style={{ fontSize: 10, color: "rgba(0,0,0,0.45)", marginTop: 2, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          "{res.source_quote}"
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                      {res.certainty > 0 && (
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
                          background: "rgba(0,0,0,0.06)",
                          fontFamily: "'IBM Plex Mono', monospace",
                        }}>
                          {(res.certainty * 100).toFixed(0)}% certain
                        </span>
                      )}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round"
                        style={{ opacity: 0.25, transition: "transform 0.2s ease", transform: isExpanded ? "rotate(180deg)" : "rotate(0)" }}>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </div>
                  </div>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div style={{ padding: "0 18px 20px", animation: "fadeSlideIn 0.3s ease" }}>
                      <style>{`@keyframes fadeSlideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>

                      {/* Mechanism */}
                      {res.mechanism && (
                        <div style={{
                          padding: "10px 14px", borderRadius: 8, marginBottom: 14,
                          background: "linear-gradient(135deg, rgba(99,102,241,0.04), rgba(16,185,129,0.04))",
                          borderLeft: "3px solid rgba(0,0,0,0.1)",
                        }}>
                          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "rgba(0,0,0,0.35)", marginBottom: 4 }}>
                            Mechanism
                          </div>
                          <div style={{ fontSize: 11.5, lineHeight: 1.55, color: "rgba(0,0,0,0.65)" }}>
                            {res.mechanism}
                          </div>
                        </div>
                      )}

                      {/* 4 resolution paths */}
                      {res.paths.length > 0 ? (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                          {res.paths.map(path => (
                            <PathCard
                              key={path.type}
                              path={path}
                              isRecommended={res.recommended === path.type}
                              isSelected={selectedPath === path.type}
                              onSelect={() => setSelections(prev => ({ ...prev, [res.variable_name]: path.type }))}
                            />
                          ))}
                        </div>
                      ) : (
                        <div style={{ textAlign: "center", padding: "20px 0", color: "rgba(0,0,0,0.3)", fontSize: 12 }}>
                          Could not resolve against dataset — backend may be offline
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
